import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { FaGithub, FaUnlink } from 'react-icons/fa';
import { BiLinkExternal } from "react-icons/bi";
import { db, functions, httpsCallable, updateAllReposToGroup, getTeamAllRepo, removeRepoFromGroup, switchActiveRepo, removeActiveRepo } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import MyHeader from "../../components/common/MyHeader";
import { useNavigate } from 'react-router-dom';
import GoBackButton from "./components/GoBackButton";
import GitHubSetupProgress from '../../components/common/GitHubSetupProgress';
import ConfirmDialog from '../../components/common/ConfirmDialog';


function RepoSetupPage() {
  const { user, groupInfo, groupActiveRepo, setGroupActiveRepo, groupMembersLoading, groupMembers } = useAuth();
  const [loading, setLoading] = useState(false);
  const [repositories, setRepositories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const appSlug = "TeamCAST-2-0";
  const navigate = useNavigate();

  const cleanupRepos = async (installationId) => {
    try {
      const checkInstallation = httpsCallable(functions, "checkInstallationExists");
      const result = await checkInstallation({ installation_id: installationId });

      if (!result.data.ok) {
        const allRepoMap = await getTeamAllRepo(groupInfo.groupId);
        const staleRepos = Object.values(allRepoMap || {}).filter(r => r.installation_id === installationId);
        for (const repo of staleRepos) {
          await removeRepoFromGroup(groupInfo.groupId, repo.repo.id);
        }
      }
    } catch (err) {
      console.warn("Cleanup failed:", err);
    }
  };

  const currentGithubUsername = !groupMembersLoading && groupMembers?.length > 0 && user
    ? groupMembers.find(m => m.email === user.email)?.github_username
    : null;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const installationId = params.get("installation_id");

    if (installationId && groupInfo) {
      autoFetchRepos(installationId)
      // Clear the URL parameters after capturing them
      window.history.replaceState({}, document.title, window.location.pathname);

    }
  }, [groupInfo]);

  useEffect(() => {
    if (!groupInfo?.groupId) return;
    if (currentGithubUsername) {
      //console.log("Current GitHub username:", currentGithubUsername);
    }

    const unsubscribe = onSnapshot(doc(db, "Groups", groupInfo.groupId), (docSnap) => {
      if (!docSnap.exists()) return;
      const data = docSnap.data();
      const activeRepo = data?.github?.active_repo || null;
      setGroupActiveRepo(activeRepo); // update state in real-time

      const allRepos = Object.values(data?.github?.all_repos || {});
      setRepositories(allRepos);
    });

    return () => unsubscribe(); // Clean up when unmounting
  }, [groupInfo?.groupId]);


  useEffect(() => {
    if (!groupInfo?.groupId || !user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const pendingId = localStorage.getItem("pendingCleanupInstallationId");
        if (pendingId) {
          //console.log("Tab focused again — checking installation cleanup...");
          cleanupRepos(pendingId);
          localStorage.removeItem("pendingCleanupInstallationId");
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [groupInfo?.groupId, user]);

  const renderedRepoCards = useMemo(() => {
    return repositories.map((entry) => {
      const repo = entry.repo;
      const isActive = groupActiveRepo?.repo.id === repo.id;

      return (
        <RepoCard key={repo.id} $active={isActive}>
          <RepoName $active={isActive}>{repo.full_name}</RepoName>
          <RepoDescription>{repo.description || 'No description provided.'}</RepoDescription>
          <RepoBranch>
            Default Branch: <strong>{repo.default_branch}</strong>
          </RepoBranch>
          <RepoPrivacy $private={repo.private}>
            Visibility: <strong><span>{repo.private ? 'Private' : 'Public'}</span></strong>
          </RepoPrivacy>
          <RepoCardFooter>
            <RepoLink href={repo.html_url} target="_blank" rel="noopener noreferrer">
              View on GitHub <BiLinkExternal />
            </RepoLink>
            <ActionGroup>
              {!isActive && (
                <Button className="green" onClick={() => handleSwitch(repo.id)}>
                  Activate Repo
                </Button>
              )}
              {entry.repo.owner?.login === currentGithubUsername && (
                <Button className="red" onClick={() => handleDisconnect(entry)}>
                  <FaUnlink className="mr-2" /> Disconnect
                </Button>
              )}
            </ActionGroup>
          </RepoCardFooter>
        </RepoCard>
      );
    });
  }, [repositories, groupActiveRepo, currentGithubUsername]);

  if (!user) {
    return <Container><Card>Please log in to continue.</Card></Container>;
  }

  if (!groupInfo) {
    return <Container><Card>Loading group information...</Card></Container>;
  }

  // const isFullyConnected = !!groupActiveRepo?.installation_id && !!groupActiveRepo?.repo;
  //const currentInstallationId = groupActiveRepo?.installation_id || tempInstallationId;

  const handleConnectGitHubApp = () => {
    if (!groupInfo?.groupId) {
      setError("Group information not loaded yet.");
      return;
    }

    const redirectUrl = `https://github.com/apps/${appSlug}/installations/new?state=${groupInfo.groupId}`;
    window.location.href = redirectUrl;
  };

  const autoFetchRepos = async (installationId) => {
    if (!installationId || !groupInfo?.groupId) {
      setError("Missing GitHub installation ID or group ID.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      //console.log("Installation ID:", installationId);

      if (!installationId) {
        console.error("No installation ID found.");
        return; // or show a message to the user
      }

      const getRepos = httpsCallable(functions, "getGitHubRepos");

      const result = await getRepos({ installation_id: installationId });
      const repos = result.data || [];


      await updateAllReposToGroup(groupInfo.groupId, installationId, repos);
      // Update state -- see how to update
      //console.log("GitHub repositories saved/updated to Firestore.");
    } catch (err) {
      console.error("Error fetching repos:", err);
      setError("Failed to load repositories. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (repoEntry) => {
    if (!groupInfo?.groupId || !repoEntry?.repo?.id) return;
    const installationId = repoEntry.installation_id;

    // If installation ID is missing or invalid, just remove repo directly
    if (!installationId) {
      console.warn("⚠️ No installation ID found. Removing repo only from Firestore.");
      setLoading(true);
      try {
        await removeRepoFromGroup(groupInfo.groupId, repoEntry.repo.id);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Fetch current list before deletion
    const reposForInstallation = Object.values(repositories || {}).filter(
      r => r?.installation_id === installationId
    );

    if (reposForInstallation.length > 1) {
      setPendingConfirm({
        title: "Disconnect Repository?",
        message: "You'll be redirected to GitHub to manage the app installation manually.",
        confirmText: "Yes, Disconnect",
        footnote: null,
        onConfirm: async () => {
          setPendingConfirm(null);
          // Multiple repos under the same installation — redirect user to GitHub settings
          const manageUrl = `https://github.com/settings/installations/${installationId}?state=${groupInfo.groupId}`;
          //window.location.href = manageUrl;
          localStorage.setItem("pendingCleanupInstallationId", installationId);
          window.open(manageUrl, "_blank");
        },
      });
      return;
    }

    // If only one repo — uninstall GitHub App directly
    setPendingConfirm({
      title: "Disconnect Repository?",
      message: "This is the only connected repository. Disconnecting it will permanently uninstall the GitHub App from your account.",
      confirmText: "Yes, Disconnect",
      footnote: "This action cannot be undone. You can reinstall the GitHub App at any time.",
      onConfirm: async () => {
        setPendingConfirm(null);
        try {
          setLoading(true);
          const success = await removeRepoFromGroup(groupInfo.groupId, repoEntry.repo.id);
          if (!success) {
            setSuccess(false);
            throw new Error("Failed to remove repo from group.");
          }
          const uninstall = httpsCallable(functions, "uninstallGithubApp");
          await uninstall({ installation_id: installationId });
          //console.log("✅ Uninstalled GitHub App (only one repo left).");
        } catch (err) {
          console.error("Disconnect error:", err);
          setError("Failed to disconnect repo.");
          setSuccess(false);
        } finally {
          // Refresh Firestore-based UI states
          setSuccess(true);
          setLoading(false);
        }
      },
    });
  };

  const handleSwitch = (repoId) => {
    if (!groupInfo?.groupId || !repoId) return;

    setPendingConfirm({
      title: "Activate Repository?",
      message: "This repo will be used for future PRs and will replace the current active one if any.",
      confirmText: "Yes, Activate",
      footnote: null,
      onConfirm: async () => {
        setPendingConfirm(null);
        try {
          setLoading(true);
          setError(null);
          const success = await switchActiveRepo(groupInfo.groupId, repoId);
          if (success) {
            // Update activeRepo only
            setSuccess(true);
          } else {
            setSuccess(false);
            throw new Error("Switch active repo failed");
          }
        } catch (err) {
          console.error("Switch error:", err);
          setError("Failed to switch active repo.");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleDeactivate = () => {
    if (!groupInfo?.groupId) return;

    setPendingConfirm({
      title: "Deactivate Repository?",
      message: "This repository will no longer be your active repo.",
      confirmText: "Yes, Deactivate",
      footnote: null,
      onConfirm: async () => {
        setPendingConfirm(null);
        try {
          setLoading(true);
          await removeActiveRepo(groupInfo.groupId);
        } catch (err) {
          console.error("Deactivate error:", err);
          setError("Failed to deactivate repo.");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleUpdateUsernameClick = () => {
    navigate('/group-setup');
  };



  return (
    <>
      <MyHeader />
      {pendingConfirm && (
        <ConfirmDialog
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmText={pendingConfirm.confirmText}
          cancelText="No, Go Back"
          variant="danger"
          showIcon={true}
          footnote={pendingConfirm.footnote}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
      <PageWrapper>
        <Container>
          <GoBackButton />
          <GitHubSetupProgress
            currentStep={2}
            totalSteps={2}
            labels={['Usernames', 'Repository']}
          />
          <Header>
            <Subtitle>Connect your group's GitHub repository for enhanced collaboration</Subtitle>
          </Header>

          <Card>
            {groupActiveRepo?.repo?.id && (
              <ActiveRepoCard>
                <RepoTitle>Active Repository</RepoTitle>
                <RepoName>{groupActiveRepo.repo.full_name}</RepoName>
                <RepoBranch>
                  Default Branch: <strong>{groupActiveRepo.repo.default_branch}</strong>
                </RepoBranch>
                <RepoPrivacy $private={groupActiveRepo.repo.private}>
                  Visibility: <strong><span>{groupActiveRepo.repo.private ? 'Private' : 'Public'}</span></strong>
                </RepoPrivacy>
                <RepoCardFooter>
                  <RepoLink href={groupActiveRepo.repo.html_url} target="_blank" rel="noopener noreferrer">
                    View on GitHub <BiLinkExternal />
                  </RepoLink>
                  <ActionGroup>
                    <Button className="red" onClick={() => handleDeactivate()}>
                      Deactivate Repo
                    </Button>
                  </ActionGroup>
                </RepoCardFooter>
              </ActiveRepoCard>
            )}

            <RepoSection>
              <SectionTitle>Repositories</SectionTitle>
              {repositories.length === 0 ? (
                <EmptyText>You haven’t connected any GitHub repositories yet.</EmptyText>
              ) : (
                renderedRepoCards
              )}
            </RepoSection>

            <FooterCard>
              <FooterTitle>Connect GitHub App</FooterTitle>
              <FooterCardText>
                Click below to install the TeamCAST GitHub App and add repo for your group.
              </FooterCardText>
              <ConnectButton onClick={() => handleConnectGitHubApp()}>
                <FaGithub className="icon" />
                Connect GitHub Repo
              </ConnectButton>
            </FooterCard>

            <FooterCard>
              <UpdateWrapper>
                <TextGroup>
                  <UpdateText>Update GitHub Username</UpdateText>
                  <UpdateSubtitle>Make sure all teammates are connected</UpdateSubtitle>
                </TextGroup>
                <UpdateButton onClick={() => handleUpdateUsernameClick()}>Update</UpdateButton>
              </UpdateWrapper>

            </FooterCard>
          </Card>
          <Footer>
            <FooterText>
              Need help? Check out our{" "}
              <FooterLink
                href="https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                documentation
              </FooterLink>
            </FooterText>
          </Footer>
        </Container>
      </PageWrapper >
    </>

  );
}

export default RepoSetupPage;

// Styled Components
const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    padding: 15px 20px;
`;

const Container = styled.div`
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;    
    padding-right: 1rem;    
    padding-top: 3rem;     
    padding-bottom: 3rem;  
    max-width: 56rem;      
`;

const Header = styled.div`
text-align: center;
  margin-bottom: 3rem; /* mb-12 */
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem; /* mb-4 */
`;

const Title = styled.h1`
    font-size: 2.25rem;
  font-weight: 700;   
  color: #1f2937;    
`;

const Subtitle = styled.p`
    font-size: 1.125rem; 
  color: #4b5563;     
`;

const Card = styled.div`
    background: #fff;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const ActiveRepoCard = styled.article`
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2.5rem;
  border: 1px solid #86efac; /* green-300 */
  text-align: left;
`;

const RepoTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  color: #16a34a; /* green-600 */
  margin-bottom: 0.5rem;
`;

const RepoSection = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937; /* gray-800 */
  margin-bottom: 1.5rem;
`;

const EmptyText = styled.p`
  text-align: center;
  color: #6b7280; /* gray-500 */
`;

const RepoCard = styled.article`
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: left;
  margin-bottom: 1.5rem;
  border: 1px solid ${({ $active }) => ($active ? '#dab2ffff' : '#e5e7eb')}; /* blue-400 or gray-200 */
`;

const RepoName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#9333ea' : '#1f2937')}; /* purple-700 or gray-800 */
  margin-bottom: 0.25rem;
`;

const RepoDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280; /* gray-600 */
  
`;

const RepoBranch = styled.p`
  color: #6b7280; /* gray-600 */
  margin-bottom: 1rem;
font-size: 0.875rem;
margin-top: 1rem;

`;


const RepoPrivacy = styled.p`
  font-size: 0.875rem;
  color: #4b5563; /* gray-600 */
  margin-bottom: 0.8rem;

  span {
    color: ${({ $private }) => ($private ? '#dc2626' : '#16a34a')}; /* red-600 or green-600 */
  }
`;

const RepoLink = styled.a`
  color: #9333ea; /* blue-600 */
  font-weight: 500;
  text-decoration: none;
  align-items: center;

  &:hover {
    color: #7e22ce; /* blue-700 */
  }

  svg {
    font-size: 1.2rem; /* match text size */
    vertical-align: middle;
    margin-bottom: 0.1rem
  }
`;

const RepoCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button`
  border: none; 
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &.green {
    background-color: #22c55e;

    &:hover {
      background-color: #16a34a;
    }
  }

  &.red {
    background-color: #ef4444;

    &:hover {
      background-color: #dc2626;
    }
  }

  &.orange {
    background-color: #9851daff;

    &:hover {
      background-color: #9333ea;
    }
  }

  .mr-2 {
    margin-right: 0.5rem;
  }
`;

const FooterCard = styled.footer`
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const FooterTitle = styled.h2`
  font-size: 1.5rem; /* text-2xl */
  font-weight: 600;  /* font-semibold */
  color: #1f2937;     /* gray-800 */
  margin-bottom: 0.75rem;
`;

const FooterCardText = styled.p`
  color: #4b5563;     /* gray-600 */
  margin-bottom: 1.5rem;
`;

const ConnectButton = styled.button`
  display: flex;
  border: none;
  align-items: center;
  justify-content: center;
  background-color: #9333ea;  
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  margin: 0 auto;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #7e22ce;
  }

  .icon {
    margin-right: 0.5rem;
  }
`;

const UpdateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.875rem; /* spacing between text and button */
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const UpdateText = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151; /* gray-700 */
`;

const UpdateSubtitle = styled.p`
  font-size: 0.875rem; /* text-sm */
  color: #6b7280; /* gray-500 */
`;

const UpdateButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  justify-content: center;
  background-color: rgb(83, 74, 245); /* blue-600 */
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgb(70, 60, 220); /* blue-700 */
  }

  .icon {
    margin-right: 0.5rem;
  }
`;


const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
`;

const FooterLink = styled.a`
  color: #7c3aed;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
